/**
 * SG Incorporation — MY와 동일하게 L_INCORPORATIONS_FILES 로 Passport / Proof of Address 업로드
 * fileOwnerType 은 DB CHAR(8) 제한
 */
import type { SgIncorporationForm } from '../components/sg/types/sgIncorporation.types';

export type SgIncorporationUploadFile = {
  fileCd: string;
  icpCd: string;
  fileType: 'PASSPORT' | 'ADDR_PROF' | 'ALLOT_DOC' | 'RC_DOC';
  fileOwnerType: 'DIRECTOR' | 'SECRETAR' | 'SHAREHOL' | 'CONTROLR' | 'SHARECAP';
  fileOwnerCd: string;
  fileName: string;
  fileData: string;
  fileSize: number;
  contentType: string;
};

export type SgPersonIdentityFileFields = {
  personCd?: string | null;
  passportFile?: File | null;
  proofOfAddressFile?: File | null;
  passportFileCd?: string | null;
  proofOfAddressFileCd?: string | null;
};

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const ALLOWED_EXT = ['.pdf', '.jpg', '.jpeg', '.png'];
const MAX_SIZE = 10 * 1024 * 1024;
const MAX_NAME = 500;

export function createEmptyPersonIdentityFiles(): Pick<
  SgPersonIdentityFileFields,
  'passportFile' | 'proofOfAddressFile' | 'passportFileCd' | 'proofOfAddressFileCd'
> & { personCd: string } {
  return {
    personCd: newFileCd(),
    passportFile: null,
    proofOfAddressFile: null,
    passportFileCd: null,
    proofOfAddressFileCd: null,
  };
}

export function ensurePersonCd(existing?: string | null): string {
  const cd = String(existing || '').trim();
  return cd || newFileCd();
}

/** secure UUID — template 스코프에서 crypto 미노출 대응 */
export function newFileCd(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function validateIncorporationUploadFile(file: File): string | null {
  if (file.size > MAX_SIZE) return 'File size must be less than 10MB';
  const name = String(file.name || '').toLowerCase();
  const extOk = ALLOWED_EXT.some((ext) => name.endsWith(ext));
  const type = String(file.type || '').toLowerCase();
  // Windows 등에서 MIME 이 빈 문자열인 경우 확장자로 허용
  const typeOk = !type || ALLOWED_TYPES.includes(type);
  if (!typeOk || !extOk) return 'Only PDF, JPG, JPEG, PNG files are allowed';
  if (file.name.length > MAX_NAME) return 'File name must be less than 500 characters';
  return null;
}

function guessContentType(fileName: string): string {
  const name = String(fileName || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
}

async function pushPersonFiles(
  out: SgIncorporationUploadFile[],
  person: SgPersonIdentityFileFields,
  ownerType: SgIncorporationUploadFile['fileOwnerType'],
  ownerCd: string,
): Promise<string | null> {
  if (person.passportFile) {
    const err = validateIncorporationUploadFile(person.passportFile);
    if (err) return err;
    const fileCd = person.passportFileCd || newFileCd();
    person.passportFileCd = fileCd;
    out.push({
      fileCd,
      icpCd: '',
      fileType: 'PASSPORT',
      fileOwnerType: ownerType,
      fileOwnerCd: ownerCd,
      fileName: person.passportFile.name,
      fileData: await fileToBase64(person.passportFile),
      fileSize: person.passportFile.size,
      contentType: person.passportFile.type || guessContentType(person.passportFile.name),
    });
  }
  if (person.proofOfAddressFile) {
    const err = validateIncorporationUploadFile(person.proofOfAddressFile);
    if (err) return err;
    const fileCd = person.proofOfAddressFileCd || newFileCd();
    person.proofOfAddressFileCd = fileCd;
    out.push({
      fileCd,
      icpCd: '',
      fileType: 'ADDR_PROF',
      fileOwnerType: ownerType,
      fileOwnerCd: ownerCd,
      fileName: person.proofOfAddressFile.name,
      fileData: await fileToBase64(person.proofOfAddressFile),
      fileSize: person.proofOfAddressFile.size,
      contentType: person.proofOfAddressFile.type || guessContentType(person.proofOfAddressFile.name),
    });
  }
  return null;
}

/**
 * Complete 요청용 files[] 수집. 실패 시 errorMessage 반환.
 * side-effect: personCd / fileCd 를 폼 행에 고정한다.
 */
export async function collectSgIncorporationFiles(
  form: SgIncorporationForm,
): Promise<{ files: SgIncorporationUploadFile[]; errorMessage: string | null }> {
  const files: SgIncorporationUploadFile[] = [];

  if (form.includeDirectors !== 'no') {
    for (const director of form.directors || []) {
      const ownerCd = ensurePersonCd((director as any).directorCd);
      (director as any).directorCd = ownerCd;
      const err = await pushPersonFiles(files, director as any, 'DIRECTOR', ownerCd);
      if (err) return { files: [], errorMessage: err };
    }
  }

  if (form.includeSecretary !== 'no' && form.secretary) {
    const ownerCd = ensurePersonCd((form.secretary as any).secretaryCd);
    (form.secretary as any).secretaryCd = ownerCd;
    const err = await pushPersonFiles(files, form.secretary as any, 'SECRETAR', ownerCd);
    if (err) return { files: [], errorMessage: err };
  }

  if (form.includeShareholders !== 'no') {
    for (const sh of form.shareholders || []) {
      if (sh.shareholderType !== 'individual') continue;
      const ownerCd = ensurePersonCd((sh as any).shareholderCd);
      (sh as any).shareholderCd = ownerCd;
      const err = await pushPersonFiles(files, sh as any, 'SHAREHOL', ownerCd);
      if (err) return { files: [], errorMessage: err };
    }
  }

  if (form.includeControllers !== 'no') {
    for (const ctrl of form.individualControllers || []) {
      const ownerCd = ensurePersonCd((ctrl as any).controllerCd);
      (ctrl as any).controllerCd = ownerCd;
      const err = await pushPersonFiles(files, ctrl as any, 'CONTROLR', ownerCd);
      if (err) return { files: [], errorMessage: err };
    }
    // 컨트롤러 supportingDocuments (개인+법인, 최대 2)
    const allCtrls = [...(form.corporateControllers || []), ...(form.individualControllers || [])];
    for (const ctrl of allCtrls) {
      for (const doc of (ctrl as any).supportingDocs || []) {
        if (!doc.file) continue;
        const err = validateIncorporationUploadFile(doc.file);
        if (err) return { files: [], errorMessage: err };
        const fileCd = doc.fileCd || newFileCd();
        doc.fileCd = fileCd;
        files.push({
          fileCd,
          icpCd: '',
          fileType: 'RC_DOC',
          fileOwnerType: 'CONTROLR',
          fileOwnerCd: fileCd,
          fileName: doc.file.name,
          fileData: await fileToBase64(doc.file),
          fileSize: doc.file.size,
          contentType: doc.file.type || guessContentType(doc.file.name),
        });
      }
    }
  }

  // 주식 자본 첨부문서 (allotment method 문서) — 통화별
  for (const cur of form.shareStructure?.currencies || []) {
    if (!cur.allotmentDocFile) continue;
    const err = validateIncorporationUploadFile(cur.allotmentDocFile);
    if (err) return { files: [], errorMessage: err };
    const fileCd = cur.allotmentDocFileCd || newFileCd();
    cur.allotmentDocFileCd = fileCd;
    files.push({
      fileCd,
      icpCd: '',
      fileType: 'ALLOT_DOC',
      fileOwnerType: 'SHARECAP',
      fileOwnerCd: fileCd,
      fileName: cur.allotmentDocFile.name,
      fileData: await fileToBase64(cur.allotmentDocFile),
      fileSize: cur.allotmentDocFile.size,
      contentType: cur.allotmentDocFile.type || guessContentType(cur.allotmentDocFile.name),
    });
  }

  return { files, errorMessage: null };
}

/** JSON 직렬화 전 File 객체 제거 + fileCd 메타만 유지
 *  Vue Proxy / File 은 structuredClone 불가 → JSON round-trip 사용
 */
export function stripSgFormFileBlobs<T extends SgIncorporationForm>(form: T): T {
  const prepared = {
    ...form,
    directors: (form.directors || []).map((row) => stripPersonFiles(row as any)),
    secretary: stripPersonFiles((form.secretary || {}) as any),
    shareholders: (form.shareholders || []).map((row) => stripPersonFiles(row as any)),
    corporateControllers: (form.corporateControllers || []).map((row) => stripControllerDocs(row as any)),
    individualControllers: (form.individualControllers || []).map((row) => stripControllerDocs(stripPersonFiles(row as any))),
    shareStructure: form.shareStructure
      ? {
          ...form.shareStructure,
          currencies: (form.shareStructure.currencies || []).map((c) => stripCurrencyFile(c as any)),
        }
      : form.shareStructure,
  };
  return JSON.parse(JSON.stringify(prepared)) as T;
}

function stripControllerDocs<T extends Record<string, unknown>>(ctrl: T): T {
  const next = { ...ctrl } as any;
  if (Array.isArray(next.supportingDocs)) {
    next.supportingDocs = next.supportingDocs.map((d: any) => ({
      fileCd: d.fileCd || (d.file ? newFileCd() : null),
    }));
  }
  return next;
}

function stripCurrencyFile<T extends Record<string, unknown>>(cur: T): T {
  const next = { ...cur } as any;
  if (next.allotmentDocFile && !next.allotmentDocFileCd) next.allotmentDocFileCd = newFileCd();
  delete next.allotmentDocFile;
  return next;
}

function stripPersonFiles<T extends Record<string, unknown>>(row: T): T {
  const next = { ...row } as any;
  if (next.passportFile) {
    if (!next.passportFileCd) next.passportFileCd = newFileCd();
  }
  if (next.proofOfAddressFile) {
    if (!next.proofOfAddressFileCd) next.proofOfAddressFileCd = newFileCd();
  }
  delete next.passportFile;
  delete next.proofOfAddressFile;
  if (next.passportFileCd) {
    next.passport = { fileCd: next.passportFileCd };
  }
  if (next.proofOfAddressFileCd) {
    next.proofOfAddress = { fileCd: next.proofOfAddressFileCd };
  }
  return next;
}
